/**
 * Popup Level Manager
 *
 * Singleton composable that tracks all currently open popups and
 * automatically manages their stacking level (z-index offset).
 *
 * Registration order determines the level:
 *   - 1st popup opened → level 1
 *   - 2nd popup opened → level 2  (renders on top)
 *   - etc.
 */
import {computed} from 'vue'
import {useState} from '#app'

interface PopupEntry {
  id: string
}

export function usePopupLevelManager() {
  // Per-request state on the server, shared singleton per app on the client.
  // Must not be a module-scope reactive: on SSR the effect scopes of the
  // components that subscribe to it are never stopped, so a module-level
  // reactive would retain every request's component tree (and its router)
  // via Vue's global targetMap, leaking memory across requests.
  const state = useState<{openPopups: PopupEntry[]}>('popupLevelManager', () => ({
    openPopups: []
  }))

  /**
   * Returns true if the given popup is the topmost (last opened) popup.
   * Useful to decide whether the close button / backdrop should be interactive.
   */
  function isTopLevel(id: string): boolean {
    const openPopups = state.value.openPopups
    if (openPopups.length === 0) {
      return false
    }
    return openPopups[openPopups.length - 1]?.id === id
  }

  /**
   * Register a popup as open.
   * Should be called when `show` becomes true.
   */
  function registerPopup(id: string): void {
    if (!state.value.openPopups.some((p) => p.id === id)) {
      state.value.openPopups = [...state.value.openPopups, {id}]
    }
  }

  /**
   * Unregister a popup.
   * Should be called when `show` becomes false or the component is unmounted.
   */
  function unregisterPopup(id: string): void {
    const index = state.value.openPopups.findIndex((p) => p.id === id)
    if (index !== -1) {
      state.value.openPopups.splice(index, 1)
    }
  }

  /**
   * Returns the 1-based level for the given popup id.
   * Returns 0 if the popup is not currently registered (closed).
   */
  function getLevel(id: string): number {
    const index = state.value.openPopups.findIndex(p => p.id === id)
    return index === -1 ? 0 : index + 1
  }

  /** Total number of currently open popups */
  const popupCount = computed(() => state.value.openPopups.length)
  const openPopups = computed(() => [...state.value.openPopups])

  return {
    popupCount,
    openPopups,
    registerPopup,
    unregisterPopup,
    getLevel,
    isTopLevel
  }
}
